import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Table,
  Tag,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Typography,
  Spin,
  Empty,
  Space,
  Tooltip,
  Card,
} from "antd";
import {
  HistoryOutlined,
  CarOutlined,
  UserOutlined,
  CalendarOutlined,
  SearchOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { fetchHistorialAsignaciones } from "./apiService";
import dayjs from "dayjs";
import "dayjs/locale/es";

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

dayjs.locale("es");

const HistorialAsignacionesDialog = ({ open, onClose }) => {
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    accion: null,
    fechaRange: null,
  });

  const accionOptions = [
    { value: "asignacion", label: "Asignacion", color: "green" },
    { value: "desasignacion", label: "Desasignacion", color: "red" },
    { value: "cambio_principal", label: "Cambio a Principal", color: "blue" },
    { value: "quitar_principal", label: "Quitado como Principal", color: "orange" },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
      };

      if (filters.search) {
        params.search = filters.search;
      }
      if (filters.accion) {
        params.accion = filters.accion;
      }
      if (filters.fechaRange && filters.fechaRange.length === 2) {
        params.fecha_inicio = filters.fechaRange[0].format("YYYY-MM-DD");
        params.fecha_fin = filters.fechaRange[1].format("YYYY-MM-DD");
      }

      const response = await fetchHistorialAsignaciones(params);
      const { data, pagination: paginationData } = response.data;

      setHistorial(data || []);
      setPagination((prev) => ({
        ...prev,
        total: paginationData?.total || 0,
      }));
    } catch (error) {
      console.error("Error al obtener historial:", error);
      setHistorial([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, filters]);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  const handleTableChange = (pag) => {
    setPagination((prev) => ({
      ...prev,
      current: pag.current,
      pageSize: pag.pageSize,
    }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleAccionChange = (value) => {
    setFilters((prev) => ({ ...prev, accion: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleDateRangeChange = (dates) => {
    setFilters((prev) => ({ ...prev, fechaRange: dates }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const getAccionTag = (accion, accionLabel, accionColor) => {
    const colorMap = {
      asignacion: "green",
      desasignacion: "red",
      cambio_principal: "blue",
      quitar_principal: "orange",
    };

    const labelMap = {
      asignacion: "Asignacion",
      desasignacion: "Desasignacion",
      cambio_principal: "Cambio a Principal",
      quitar_principal: "Quitado Principal",
    };

    return (
      <Tag color={accionColor || colorMap[accion] || "default"}>
        {accionLabel || labelMap[accion] || accion}
      </Tag>
    );
  };

  const columns = [
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      width: 140,
      render: (fecha) => (
        <Tooltip title={dayjs(fecha).format("DD/MM/YYYY HH:mm:ss")}>
          <Space direction="vertical" size={0}>
            <Text strong>{dayjs(fecha).format("DD/MM/YYYY")}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(fecha).format("HH:mm")}
            </Text>
          </Space>
        </Tooltip>
      ),
    },
    {
      title: "Accion",
      dataIndex: "accion",
      key: "accion",
      width: 150,
      render: (accion, record) =>
        getAccionTag(accion, record.accion_label, record.accion_color),
    },
    {
      title: "Vehiculo",
      key: "vehiculo",
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <CarOutlined style={{ color: "#1890ff" }} />
            <Text strong>{record.vehiculo_placa || "-"}</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.vehiculo_codigo || ""}
            {record.vehiculo_marca && ` - ${record.vehiculo_marca}`}
            {record.vehiculo_modelo && ` ${record.vehiculo_modelo}`}
          </Text>
        </Space>
      ),
    },
    {
      title: "Conductor",
      key: "conductor",
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <UserOutlined style={{ color: "#52c41a" }} />
            <Text strong>{record.conductor_nombre || "-"}</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.conductor_documento || ""}
          </Text>
        </Space>
      ),
    },
    {
      title: "Principal",
      dataIndex: "es_principal",
      key: "es_principal",
      width: 90,
      align: "center",
      render: (esPrincipal) =>
        esPrincipal ? (
          <Tag color="gold">Principal</Tag>
        ) : (
          <Tag>No</Tag>
        ),
    },
    {
      title: "Usuario",
      dataIndex: "usuario_nombre",
      key: "usuario_nombre",
      width: 150,
      render: (nombre) => nombre || <Text type="secondary">Sistema</Text>,
    },
    {
      title: "Descripcion",
      dataIndex: "descripcion",
      key: "descripcion",
      ellipsis: true,
      render: (desc) => (
        <Tooltip title={desc}>
          <Text ellipsis style={{ maxWidth: 250 }}>
            {desc}
          </Text>
        </Tooltip>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <HistoryOutlined style={{ color: "#1890ff" }} />
          <span>Historial de Asignaciones</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={1200}
      centered
      styles={{ body: { padding: "16px 24px" } }}
    >
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Input
              placeholder="Buscar por placa, codigo, conductor..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={handleSearchChange}
              allowClear
            />
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Filtrar por accion"
              value={filters.accion}
              onChange={handleAccionChange}
              allowClear
              style={{ width: "100%" }}
              suffixIcon={<FilterOutlined />}
            >
              {accionOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  <Tag color={opt.color} style={{ marginRight: 8 }}>
                    {opt.label}
                  </Tag>
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={10}>
            <RangePicker
              value={filters.fechaRange}
              onChange={handleDateRangeChange}
              format="DD/MM/YYYY"
              placeholder={["Fecha inicio", "Fecha fin"]}
              style={{ width: "100%" }}
              suffixIcon={<CalendarOutlined />}
            />
          </Col>
        </Row>
      </Card>

      <Spin spinning={loading}>
        {historial.length === 0 && !loading ? (
          <Empty
            description="No se encontraron registros de historial"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={historial}
            rowKey="id"
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} registros`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            onChange={handleTableChange}
            scroll={{ x: 1000 }}
            size="small"
          />
        )}
      </Spin>
    </Modal>
  );
};

export default HistorialAsignacionesDialog;
